'use server'

import { apiRequest, ApiResponse } from '../request'

// ── Type Enums ───────────────────────────────────────────────────────────────

export type SalesGoalType =
    | 'REVENUE'
    | 'DEALS_WON'
    | 'DEALS_CREATED'
    | 'LEADS_CONVERTED'
    | 'AVG_CLOSE_TIME'
    | 'CONVERSION_RATE'
    | 'FOLLOW_UPS_SENT'
    | 'ACTIVITIES'

export type SalesGoalPeriod = 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY' | 'CUSTOM'

export type SalesGoalScope = 'INDIVIDUAL' | 'WALLET' | 'COMPANY'

export type SalesGoalStatus = 'ACTIVE' | 'COMPLETED' | 'FAILED' | 'CANCELLED'

export type BadgeRarity = 'BRONZE' | 'SILVER' | 'GOLD' | 'DIAMOND'

export type RankingPeriod = 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ALL_TIME'

// ── Label Maps ───────────────────────────────────────────────────────────────

export const GOAL_TYPE_LABELS: Record<SalesGoalType, string> = {
    REVENUE: 'Receita',
    DEALS_WON: 'Negócios Ganhos',
    DEALS_CREATED: 'Negócios Criados',
    LEADS_CONVERTED: 'Leads Convertidos',
    AVG_CLOSE_TIME: 'Tempo Médio de Fechamento',
    CONVERSION_RATE: 'Taxa de Conversão',
    FOLLOW_UPS_SENT: 'Follow-ups Enviados',
    ACTIVITIES: 'Atividades',
}

export const GOAL_TYPE_UNITS: Record<SalesGoalType, string> = {
    REVENUE: 'R$',
    DEALS_WON: 'negócios',
    DEALS_CREATED: 'negócios',
    LEADS_CONVERTED: 'leads',
    AVG_CLOSE_TIME: 'dias',
    CONVERSION_RATE: '%',
    FOLLOW_UPS_SENT: 'follow-ups',
    ACTIVITIES: 'atividades',
}

export const GOAL_PERIOD_LABELS: Record<SalesGoalPeriod, string> = {
    WEEKLY: 'Semanal',
    MONTHLY: 'Mensal',
    QUARTERLY: 'Trimestral',
    YEARLY: 'Anual',
    CUSTOM: 'Personalizado',
}

export const GOAL_SCOPE_LABELS: Record<SalesGoalScope, string> = {
    INDIVIDUAL: 'Individual',
    WALLET: 'Carteira',
    COMPANY: 'Empresa',
}

export const GOAL_STATUS_LABELS: Record<SalesGoalStatus, string> = {
    ACTIVE: 'Ativa',
    COMPLETED: 'Concluída',
    FAILED: 'Não Atingida',
    CANCELLED: 'Cancelada',
}

export const GOAL_STATUS_COLORS: Record<SalesGoalStatus, string> = {
    ACTIVE: 'badge-info',
    COMPLETED: 'badge-success',
    FAILED: 'badge-error',
    CANCELLED: 'badge-ghost',
}

export const BADGE_RARITY_LABELS: Record<BadgeRarity, string> = {
    BRONZE: 'Bronze',
    SILVER: 'Prata',
    GOLD: 'Ouro',
    DIAMOND: 'Diamante',
}

export const BADGE_RARITY_COLORS: Record<BadgeRarity, string> = {
    BRONZE: '#CD7F32',
    SILVER: '#C0C0C0',
    GOLD: '#FFD700',
    DIAMOND: '#B9F2FF',
}

export const RANKING_PERIOD_LABELS: Record<RankingPeriod, string> = {
    WEEKLY: 'Semanal',
    MONTHLY: 'Mensal',
    QUARTERLY: 'Trimestral',
    ALL_TIME: 'Geral',
}

// ── Interfaces ───────────────────────────────────────────────────────────────

export interface SalesGoal {
    id: number
    name: string
    description?: string
    type: SalesGoalType
    targetValue: number
    currentValue: number
    scope: SalesGoalScope
    userId?: number
    user?: { id: number; name: string }
    walletId?: number
    wallet?: { id: number; name: string }
    period: SalesGoalPeriod
    startDate: string
    endDate: string
    isRecurring: boolean
    status: SalesGoalStatus
    completedAt?: string
    companyId: number
    createdAt: string
    updatedAt: string
}

export interface SalesBadgeDefinition {
    id: number
    key: string
    name: string
    description: string
    icon: string
    rarity: BadgeRarity
    category: string
    criteria: any
    isSystem: boolean
    active: boolean
    createdAt: string
}

export interface SalesBadgeEarned {
    id: number
    badgeId: number
    badge: SalesBadgeDefinition
    userId: number
    user?: { id: number; name: string }
    companyId: number
    earnedAt: string
    metadata?: any
}

export interface SalesPointLog {
    id: number
    userId: number
    user?: { id: number; name: string }
    points: number
    reason: string
    referenceType?: string
    referenceId?: number
    companyId: number
    createdAt: string
}

export interface SalesRanking {
    id: number
    userId: number
    user?: { id: number; name: string }
    period: RankingPeriod
    periodStart: string
    periodEnd: string
    totalPoints: number
    totalRevenue: number
    dealsWon: number
    conversionRate: number
    rank: number
    previousRank?: number
    companyId: number
    updatedAt: string
}

export interface UserGamificationProfile {
    userId: number
    userName: string
    totalPoints: number
    rank: number
    badges: SalesBadgeEarned[]
    activeGoals: SalesGoal[]
    recentPoints: SalesPointLog[]
}

export interface CreateGoalData {
    name: string
    description?: string
    type: SalesGoalType
    targetValue: number
    scope: SalesGoalScope
    userId?: number
    walletId?: number
    period: SalesGoalPeriod
    startDate: string
    endDate: string
    isRecurring?: boolean
}

export interface UpdateGoalData {
    name?: string
    description?: string
    targetValue?: number
    endDate?: string
    isRecurring?: boolean
}

export interface GoalFilters {
    status?: SalesGoalStatus
    type?: SalesGoalType
    scope?: SalesGoalScope
    userId?: number
}

export interface BadgesResponse {
    definitions: SalesBadgeDefinition[]
    earned: SalesBadgeEarned[]
}

// ── API Functions ────────────────────────────────────────────────────────────

export async function getGamificationProfile(): Promise<ApiResponse<UserGamificationProfile>> {
    return apiRequest<UserGamificationProfile>('sales/gamification/profile', {})
}

export async function getUserProfile(userId: number): Promise<ApiResponse<UserGamificationProfile>> {
    return apiRequest<UserGamificationProfile>(`sales/gamification/profile/${userId}`, {})
}

export async function getRankings(period?: RankingPeriod): Promise<ApiResponse<SalesRanking[]>> {
    return apiRequest<SalesRanking[]>('sales/gamification/rankings', { params: { period } })
}

export async function getBadges(): Promise<ApiResponse<BadgesResponse>> {
    return apiRequest<BadgesResponse>('sales/gamification/badges', {})
}

export async function getPointHistory(page?: number, limit?: number): Promise<ApiResponse<SalesPointLog[]>> {
    return apiRequest<SalesPointLog[]>('sales/gamification/points', { params: { page, limit } })
}

export async function getGoals(filters?: GoalFilters): Promise<ApiResponse<SalesGoal[]>> {
    return apiRequest<SalesGoal[]>('sales/gamification/goals', { params: filters })
}

export async function getGoal(id: number): Promise<ApiResponse<SalesGoal>> {
    return apiRequest<SalesGoal>(`sales/gamification/goals/${id}`, {})
}

export async function createGoal(data: CreateGoalData): Promise<ApiResponse<SalesGoal>> {
    return apiRequest<SalesGoal>('sales/gamification/goals', { data }, 'post')
}

export async function updateGoal(id: number, data: UpdateGoalData): Promise<ApiResponse<SalesGoal>> {
    return apiRequest<SalesGoal>(`sales/gamification/goals/${id}`, { data }, 'patch')
}

export async function cancelGoal(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`sales/gamification/goals/${id}`, {}, 'delete')
}
